'use client';

import { useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

export interface ChatMessage {
  id: string;
  leadId: string | null;
  text: string;
  direction: 'in' | 'out';
  status: 'pending' | 'sent' | 'delivered' | 'read';
  isTemplate: boolean;
  templateName: string | null;
  createdAt: string;
  senderPhone: string | null;
}

export interface ChatConversation {
  id: string;
  name: string;
  phone: string;
  lastMessage: string;
  lastTime: string;
  unread: number;
  stage: string;
  source: string;
  neetScore: number;
  optedIn: boolean;
}

function mapMessageRow(row: Record<string, unknown>): ChatMessage {
  return {
    id: row.id as string,
    leadId: (row.lead_id as string) ?? null,
    text: row.text as string,
    direction: row.direction as 'in' | 'out',
    status: row.status as ChatMessage['status'],
    isTemplate: (row.is_template as boolean) ?? false,
    templateName: (row.template_name as string) ?? null,
    createdAt: row.created_at as string,
    senderPhone: (row.sender_phone as string) ?? null,
  };
}

export function useRealtimeChat(leadId: string | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchMessages = useCallback(async (id: string) => {
    setLoading(true);
    const supabase = createClient();
    const { data } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('lead_id', id)
      .order('created_at', { ascending: true });

    setMessages((data ?? []).map(mapMessageRow));
    setLoading(false);
  }, []);

  const sendMessage = useCallback(
    async (text: string, isTemplate = false, templateName: string | null = null) => {
      if (!leadId) return null;
      const supabase = createClient();
      const { data, error } = await supabase
        .from('chat_messages')
        .insert({
          lead_id: leadId,
          text,
          direction: 'out',
          status: 'sent',
          is_template: isTemplate,
          template_name: templateName,
        })
        .select()
        .single();

      if (error) return error;
      return null;
    },
    [leadId]
  );

  useEffect(() => {
    if (!leadId) {
      setMessages([]);
      return;
    }
    fetchMessages(leadId);

    const supabase = createClient();
    const channel = supabase
      .channel(`chat_messages_${leadId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_messages',
          filter: `lead_id=eq.${leadId}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setMessages((prev) => [...prev, mapMessageRow(payload.new as Record<string, unknown>)]);
          } else if (payload.eventType === 'UPDATE') {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === (payload.new as Record<string, unknown>).id
                  ? mapMessageRow(payload.new as Record<string, unknown>)
                  : m
              )
            );
          } else if (payload.eventType === 'DELETE') {
            setMessages((prev) =>
              prev.filter((m) => m.id !== (payload.old as Record<string, unknown>).id)
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [leadId, fetchMessages]);

  return { messages, loading, sendMessage, refetch: () => leadId && fetchMessages(leadId) };
}

/** Hook to fetch ALL messages across all leads — for the global log view */
export function useAllMessages() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const supabase = createClient();
    const { data, error } = await supabase
      .from('chat_messages')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(500);

    if (error) {
      console.error('[useAllMessages] fetch error:', error);
    }
    setMessages((data ?? []).map(mapMessageRow));
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();

    const supabase = createClient();
    const channel = supabase
      .channel('all_chat_messages')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'chat_messages' },
        (payload) => {
          setMessages((prev) => [mapMessageRow(payload.new as Record<string, unknown>), ...prev]);
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'chat_messages' },
        (payload) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === (payload.new as Record<string, unknown>).id
                ? mapMessageRow(payload.new as Record<string, unknown>)
                : m
            )
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchAll]);

  return { messages, loading, refetch: fetchAll };
}
